import { Disclosure } from '@headlessui/react';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FiltersBar from '@/components/FiltersBar';
import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import FieldError from '@/forms/FieldError';
import ActiveFilters from '@/forms/NoteFilterForm/ActiveFilters';
import { Props } from '@/forms/NoteFilterForm/types';
import useNoteTypes from '@/hooks/useNoteTypes';
import Accordion from '@/ui/components/Accordion/Accordion';
import Alert from '@/ui/components/Alert';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Spinner from '@/ui/components/Spinner';

const NoteFilterForm = ({ activeFilters, setFilters, contentTypeName }: Props) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch
  } = useForm({
    defaultValues: activeFilters,
    values: activeFilters
  });
  const values = watch();
  const { data: noteTypesResponse, isPending: isLoadingNoteTypes } = useNoteTypes(
    true,
    contentTypeName
  );

  useEffect(() => {
    if (activeFilters.note_type === 'member-availability' && noteTypesResponse) {
      setFilters({
        ...activeFilters,
        note_type: noteTypesResponse?.data?.find(
          (noteType) => noteType.slug === 'member-availability'
        )?.id
      });
    }
  }, [activeFilters, noteTypesResponse, setFilters]);

  const ApplyFiltersButton = () => (
    <Disclosure.Button as="div">
      <Button className="mt-1" color={Color.INDIGO} disabled={isSubmitting} size="xs" type="submit">
        {isSubmitting ? <Spinner /> : 'Apply'}
      </Button>
    </Disclosure.Button>
  );

  const applyFilters = (): void => {
    setFilters(values);
  };

  const noteTypeSelectOptions = useMemo(
    () =>
      noteTypesResponse?.data?.map(({ id, title }) => ({
        label: title,
        value: id
      })) ?? [],
    [noteTypesResponse]
  );

  return (
    <form className="relative" onSubmit={handleSubmit(applyFilters)}>
      <FiltersBar
        filterSets={
          <div className="mb-1">
            <Accordion buttonText="Note Type">
              {errors.note_type?.message && (
                <Alert level="error" message={errors.note_type.message} />
              )}

              {isLoadingNoteTypes ? (
                <Spinner label="Loading note types..." />
              ) : (
                <InputGroup>
                  <Controller
                    name="note_type"
                    control={control}
                    render={({ field }) => {
                      return (
                        <AutocompleteSingleSelect
                          label="Note Type"
                          onChange={(selection) => field.onChange(selection?.value)}
                          options={noteTypeSelectOptions}
                          placeholder="Select a Note Type"
                          selectedOption={
                            noteTypeSelectOptions.find(
                              (noteType) => field.value === noteType.value
                            ) ?? emptySelectOption
                          }
                        />
                      );
                    }}
                    rules={{ required: 'Note Type is required' }}
                  />
                  <FieldError message={errors.note_type?.message} />
                </InputGroup>
              )}
              <ApplyFiltersButton />
            </Accordion>
          </div>
        }>
        {!!Object.entries(activeFilters).length && (
          <ActiveFilters
            filterOptions={{ noteTypes: noteTypesResponse?.data ?? [] }}
            filters={activeFilters}
            setFilters={setFilters}
            values={values}
          />
        )}
      </FiltersBar>
    </form>
  );
};

export default NoteFilterForm;
